import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Container,
    Typography,
    Grid,
    Box,
    TextField,
    IconButton,
    Tooltip,
    CircularProgress
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import DocumentViewer from '../components/DocumentViewer';
import AddClassificationDialog from '../components/AddClassificationDialog';
import ReassignDialog from '../components/ReassignDialog';
import './TrainingSetDetail.css';
import { useDropzone } from 'react-dropzone';

function ClassificationDropzone({ classification, uploading, handleFileUpload }) {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: { 'application/pdf': ['.pdf'] },
        multiple: true,
        onDrop: (acceptedFiles) => {
            if (uploading) return;
            // Create a synthetic event to simulate the file input event.
            const syntheticEvent = { target: { files: acceptedFiles } };
            handleFileUpload(classification.trainingSetClassificationId, syntheticEvent);
        }
    });

    return (
        <div {...getRootProps({ className: 'dropzone' })}>
            <input {...getInputProps()} disabled={uploading} />
            {isDragActive ? (
                <Typography variant="body2" align="center">
                    Drop the files here ...
                </Typography>
            ) : (
                <Typography variant="body2" align="center">
                    Drag & drop files here, or click to select files
                </Typography>
            )}
        </div>
    );
}

export default function TrainingSetDetail() {
    const { id } = useParams();
    const [trainingSet, setTrainingSet] = useState(null);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [classificationFilter, setClassificationFilter] = useState('');
    const [expandedAccordions, setExpandedAccordions] = useState([]);
    const [addClassificationDialogOpen, setAddClassificationDialogOpen] = useState(false);
    const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
    const [reassignOption, setReassignOption] = useState(null);

    // Fetch training set on initial load.
    useEffect(() => {
        async function fetchTrainingSet() {
            try {
                const response = await fetch(`/api/TrainingSet/${id}`, { credentials: 'include' });
                if (response.ok) {
                    const data = await response.json();
                    // For lazy loading, force documents to be undefined.
                    if (data.allowedClassifications) {
                        data.allowedClassifications = data.allowedClassifications.map(classification => ({
                            ...classification,
                            documents: undefined
                        }));
                    } else {
                        data.allowedClassifications = [];
                    }
                    setTrainingSet(data);
                    setExpandedAccordions([]);
                } else {
                    console.error('Error fetching training set');
                }
            } catch (error) {
                console.error('Error fetching training set:', error);
            }
        }
        fetchTrainingSet();
    }, [id]);

    // Lazy-load documents for a classification when its accordion is expanded.
    const lazyLoadDocuments = async (classificationId) => {
        try {
            const response = await fetch(
                `/api/TrainingSetDocuments?trainingSetClassificationId=${classificationId}`,
                {
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include'
                }
            );
            if (response.ok) {
                const docs = await response.json();
                setTrainingSet(prev => {
                    const updated = { ...prev };
                    updated.allowedClassifications = updated.allowedClassifications.map(classification => {
                        if (classification.trainingSetClassificationId === classificationId) {
                            return { ...classification, documents: docs || [] };
                        }
                        return classification;
                    });
                    return updated;
                });
            } else {
                console.error(`Error lazy loading documents for classification ${classificationId}`);
                setTrainingSet(prev => {
                    const updated = { ...prev };
                    updated.allowedClassifications = updated.allowedClassifications.map(classification => {
                        if (classification.trainingSetClassificationId === classificationId) {
                            return { ...classification, documents: [] };
                        }
                        return classification;
                    });
                    return updated;
                });
            }
        } catch (error) {
            console.error("Error lazy loading documents:", error);
            setTrainingSet(prev => {
                const updated = { ...prev };
                updated.allowedClassifications = updated.allowedClassifications.map(classification => {
                    if (classification.trainingSetClassificationId === classificationId) {
                        return { ...classification, documents: [] };
                    }
                    return classification;
                });
                return updated;
            });
        }
    };

    // Handle accordion expand/collapse.
    const handleAccordionChange = (classificationId) => async (event, isExpanded) => {
        if (isExpanded) {
            const classification = trainingSet.allowedClassifications.find(
                c => c.trainingSetClassificationId === classificationId
            );
            if (classification && classification.documents === undefined) {
                await lazyLoadDocuments(classificationId);
            }
        }
        setExpandedAccordions(prev => {
            if (isExpanded) {
                return [...prev, classificationId];
            } else {
                return prev.filter(id => id !== classificationId);
            }
        });
    };

    const handleFilterChange = (event) => {
        setClassificationFilter(event.target.value);
    };

    const handleExpandAll = () => {
        if (trainingSet) {
            const allIds = trainingSet.allowedClassifications.map(
                classification => classification.trainingSetClassificationId
            );
            setExpandedAccordions(allIds);
            // Trigger lazy-loading for all classifications.
            allIds.forEach(id => {
                const classification = trainingSet.allowedClassifications.find(c => c.trainingSetClassificationId === id);
                if (classification && classification.documents === undefined) {
                    lazyLoadDocuments(id);
                }
            });
        }
    };

    const handleCollapseAll = () => {
        setExpandedAccordions([]);
    };

    const handleFileUpload = async (classificationId, event) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;
        setUploading(true);
        try {
            const uploadPromises = Array.from(files).map(async (file) => {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('trainingSetId', id);
                formData.append('trainingSetClassificationId', classificationId);
                const response = await fetch('/api/TrainingSetDocuments/upload', {
                    method: 'POST',
                    body: formData,
                    credentials: 'include'
                });
                if (!response.ok) {
                    console.error(`Upload failed for file: ${file.name}`);
                    return null;
                }
                return await response.json();
            });
            const newDocs = await Promise.all(uploadPromises);
            const successfulDocs = newDocs.filter(doc => doc != null);
            setTrainingSet(prev => {
                const updated = { ...prev };
                updated.allowedClassifications = updated.allowedClassifications.map(classification => {
                    if (classification.trainingSetClassificationId === classificationId) {
                        return {
                            ...classification,
                            documents: [...(classification.documents || []), ...successfulDocs]
                        };
                    }
                    return classification;
                });
                return updated;
            });
        } catch (error) {
            console.error('Error uploading files:', error);
        } finally {
            setUploading(false);
        }
    };

    const openDocumentViewer = (doc) => {
        setSelectedDocument(doc);
    };

    const handleViewerClose = () => {
        setSelectedDocument(null);
    };

    const handleValidate = async () => {
        try {
            const payload = {
                parentRecordId: selectedDocument.trainingSetDocumentId,
                isCorrect: true,
                correctClassificationId: null
            };
            const response = await fetch('/api/TrainingSetDocuments/feedback', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                credentials: 'include'
            });
            if (response.ok) {
                updateDocumentValidation(selectedDocument.trainingSetDocumentId, true);
            } else {
                console.error("Feedback submission failed");
            }
        } catch (error) {
            console.error('Error validating document:', error);
        }
    };

    const handleIncorrect = () => {
        // Open the reassign dialog.
        setReassignDialogOpen(true);
    };

    const handleReassignConfirm = async () => {
        try {
            const payload = {
                parentRecordId: selectedDocument.trainingSetDocumentId,
                isCorrect: false,
                correctClassificationId: reassignOption ? reassignOption.value : null
            };
            const response = await fetch('/api/TrainingSetDocuments/feedback', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                credentials: 'include'
            });
            if (response.ok) {
                const res = await fetch(`/api/TrainingSet/${id}`, { credentials: 'include' });
                if (res.ok) {
                    const data = await res.json();
                    if (data.allowedClassifications) {
                        data.allowedClassifications = data.allowedClassifications.map(classification => ({
                            ...classification,
                            documents: undefined
                        }));
                    } else {
                        data.allowedClassifications = [];
                    }
                    setTrainingSet(data);
                    expandedAccordions.forEach(classificationId => {
                        lazyLoadDocuments(classificationId);
                    });
                }
                setReassignDialogOpen(false);
                setSelectedDocument(null);
            }
        } catch (error) {
            console.error('Error marking document as incorrect:', error);
        }
    };

    const handleReassignCancel = () => {
        setReassignDialogOpen(false);
        setReassignOption(null);
    };

    const handleDelete = async () => {
        try {
            const response = await fetch(
                `/api/TrainingSetDocuments/${selectedDocument.trainingSetDocumentId}`,
                { method: 'DELETE', credentials: 'include' }
            );
            if (response.ok) {
                removeDocumentFromState(selectedDocument.trainingSetDocumentId);
                setSelectedDocument(null);
            }
        } catch (error) {
            console.error('Error deleting document:', error);
        }
    };

    const handleNext = () => {
        if (!trainingSet || !selectedDocument) return;
        const currentClassificationIndex = trainingSet.allowedClassifications.findIndex(classification =>
            (classification.documents || []).some(doc => doc.trainingSetDocumentId === selectedDocument.trainingSetDocumentId)
        );
        if (currentClassificationIndex === -1) return;
        const currentClassification = trainingSet.allowedClassifications[currentClassificationIndex];
        const docs = currentClassification.documents || [];
        const currentDocIndex = docs.findIndex(doc => doc.trainingSetDocumentId === selectedDocument.trainingSetDocumentId);
        if (currentDocIndex !== -1 && currentDocIndex < docs.length - 1) {
            setSelectedDocument(docs[currentDocIndex + 1]);
            return;
        }
        for (let i = currentClassificationIndex + 1; i < trainingSet.allowedClassifications.length; i++) {
            const nextDocs = trainingSet.allowedClassifications[i].documents || [];
            if (nextDocs.length > 0) {
                setSelectedDocument(nextDocs[0]);
                return;
            }
        }
    };

    const updateDocumentValidation = (docId, isValidated) => {
        setTrainingSet(prev => {
            if (!prev) return prev;
            const updatedAllowedClassifications = prev.allowedClassifications.map(classification => {
                if (classification.documents === undefined) {
                    return classification;
                }
                const updatedDocs = classification.documents.map(doc => {
                    if (doc.trainingSetDocumentId === docId) {
                        return { ...doc, isValidated, validatedAt: new Date().toISOString() };
                    }
                    return doc;
                });
                return { ...classification, documents: updatedDocs };
            });
            return { ...prev, allowedClassifications: updatedAllowedClassifications };
        });
    };

    const removeDocumentFromState = (docId) => {
        setTrainingSet(prev => {
            if (!prev) return prev;
            const updatedAllowedClassifications = prev.allowedClassifications.map(classification => {
                const updatedDocs = (classification.documents || []).filter(doc => doc.trainingSetDocumentId !== docId);
                return { ...classification, documents: updatedDocs };
            });
            return { ...prev, allowedClassifications: updatedAllowedClassifications };
        });
    };

    // This callback is invoked when a document is updated (split/unsplit).
    const handleDocumentUpdated = (updateResponse, options = {}) => {
        if (options.unsplitOnly) {
            // Update both the selected document and the left panel record.
            setSelectedDocument(updateResponse);
            setTrainingSet(prev => {
                const updatedClassifications = prev.allowedClassifications.map(classification => {
                    if (classification.trainingSetClassificationId === updateResponse.trainingSetClassificationId) {
                        const updatedDocs = (classification.documents || []).map(doc => {
                            if (doc.trainingSetDocumentId === updateResponse.trainingSetDocumentId) {
                                return updateResponse;
                            }
                            return doc;
                        });
                        return { ...classification, documents: updatedDocs };
                    }
                    return classification;
                });
                return { ...prev, allowedClassifications: updatedClassifications };
            });
        } else if (updateResponse && updateResponse.original && updateResponse.split) {
            // For a split operation, update the classification's document list and select the new split record.
            setTrainingSet(prev => {
                const updatedClassifications = prev.allowedClassifications.map(classification => {
                    if (classification.trainingSetClassificationId === updateResponse.original.trainingSetClassificationId) {
                        let docs = classification.documents || [];
                        docs = docs.filter(doc => doc.trainingSetDocumentId !== updateResponse.original.trainingSetDocumentId);
                        docs.push(updateResponse.original, updateResponse.split);
                        docs.sort((a, b) => (a.startPage || 1) - (b.startPage || 1));
                        return { ...classification, documents: docs };
                    }
                    return classification;
                });
                return { ...prev, allowedClassifications: updatedClassifications };
            });
            setSelectedDocument(updateResponse.split);
        }
    };

    // Options for reassign dialog.
    const reassignOptions = trainingSet
        ? trainingSet.allowedClassifications
            .filter(c => c.documentClassification && c.documentClassification.classificationName)
            .map(c => ({
                value: c.documentClassification.classificationId,
                label: c.documentClassification.classificationName,
            }))
        : [];

    // Filter classifications based on the filter input.
    const filteredClassifications = trainingSet
        ? trainingSet.allowedClassifications.filter(classification =>
            String(classification.documentClassification?.classificationName || '')
                .toLowerCase()
                .includes(classificationFilter.toLowerCase())
        )
        : [];

    // Sort the filtered classifications by classification name.
    const sortedClassifications = filteredClassifications.sort((a, b) =>
        (a.documentClassification?.classificationName || '').localeCompare(b.documentClassification?.classificationName || '')
    );

    return (
        <Container className="training-set-detail-container">
            {trainingSet ? (
                <>
                    <Box display="flex" alignItems="center" sx={{ pb: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            Training Set: {trainingSet.name}
                        </Typography>
                    </Box>
                    <Grid container spacing={2}>
                        {/* Left Column: Toolbar and Expandable List */}
                        <Grid item xs={12} md={4}>
                            <div className="left-panel-container">
                                <Box className="left-panel-toolbar" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <TextField
                                        label="Filter Classifications"
                                        size="small"
                                        variant="outlined"
                                        value={classificationFilter}
                                        onChange={handleFilterChange}
                                        sx={{ mr: 2, width: '50%' }}
                                    />
                                    <Tooltip title="Add Classification to Training Set">
                                        <IconButton onClick={() => setAddClassificationDialogOpen(true)} sx={{ mr: 1 }}>
                                            <AddIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Expand All">
                                        <IconButton onClick={handleExpandAll} sx={{ mr: 1 }}>
                                            <UnfoldMoreIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Collapse All">
                                        <IconButton onClick={handleCollapseAll}>
                                            <UnfoldLessIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                {sortedClassifications.map(classification => (
                                    <Accordion
                                        key={classification.trainingSetClassificationId}
                                        expanded={expandedAccordions.includes(classification.trainingSetClassificationId)}
                                        onChange={handleAccordionChange(classification.trainingSetClassificationId)}
                                        className="classification-accordion"
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls={`panel-${classification.trainingSetClassificationId}-content`}
                                            id={`panel-${classification.trainingSetClassificationId}-header`}
                                        >
                                            <Typography className="classification-title">
                                                {String(classification.documentClassification?.classificationName || '')}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails className="accordion-details">
                                            <ClassificationDropzone
                                                classification={classification}
                                                uploading={uploading}
                                                handleFileUpload={handleFileUpload}
                                            />
                                            {classification.documents === undefined ? (
                                                <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 2 }}>
                                                    <CircularProgress size={20} />
                                                </Box>
                                            ) : (
                                                <div className="file-list">
                                                    {(classification.documents || []).map(doc => (
                                                        <div
                                                            key={doc.trainingSetDocumentId}
                                                            className={`file-row ${selectedDocument &&
                                                                doc.trainingSetDocumentId === selectedDocument.trainingSetDocumentId
                                                                ? 'selected'
                                                                : ''
                                                                }`}
                                                            onClick={() => openDocumentViewer(doc)}
                                                        >
                                                            <Tooltip title={doc.fileName}>
                                                                <Typography variant="body2" className="file-name">
                                                                    {doc.fileName}
                                                                </Typography>
                                                            </Tooltip>
                                                            <div className="validation-status">
                                                                {doc.isValidated ? (
                                                                    <div className="status validated">
                                                                        <Typography variant="caption">Validated</Typography>
                                                                    </div>
                                                                ) : (
                                                                    <div className="status not-validated">
                                                                        <Typography variant="caption">Not Validated</Typography>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </div>
                        </Grid>
                        {/* Right Column: PDF Viewer */}
                        <Grid item xs={12} md={8}>
                            {selectedDocument ? (
                                <DocumentViewer
                                    document={selectedDocument}
                                    onClose={handleViewerClose}
                                    onValidate={handleValidate}
                                    onIncorrect={handleIncorrect}
                                    onDelete={handleDelete}
                                    onNext={handleNext}
                                    onDocumentUpdated={handleDocumentUpdated}
                                />
                            ) : (
                                <Typography variant="h6" align="center" className="viewer-placeholder">
                                    Select a document to view
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                    <ReassignDialog
                        open={reassignDialogOpen}
                        onClose={handleReassignCancel}
                        onConfirm={handleReassignConfirm}
                        options={reassignOptions}
                        value={reassignOption}
                        onChange={setReassignOption}
                    />
                    <AddClassificationDialog
                        open={addClassificationDialogOpen}
                        onClose={() => setAddClassificationDialogOpen(false)}
                        trainingSetId={id}
                        domainId={trainingSet && trainingSet.documentDomain ? trainingSet.documentDomain.domainId : ''}
                        onAdd={(newClassification) => {
                            const transformed = {
                                trainingSetClassificationId: newClassification.trainingSetClassificationId,
                                documentClassification: {
                                    classificationId: newClassification.value,
                                    classificationName: newClassification.label,
                                },
                                documents: []
                            };
                            setTrainingSet(prev => {
                                const updated = { ...prev };
                                updated.allowedClassifications = [...(prev.allowedClassifications || []), transformed];
                                return updated;
                            });
                            setAddClassificationDialogOpen(false);
                        }}
                    />
                </>
            ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
                    <CircularProgress />
                </Box>
            )}
        </Container>
    );
}
